import { useStepContext } from './contexts/stepContext';
import { useAppSelector } from './hooks';
import { useConfigContext } from './contexts/configContext';

// Helper function to get nested property value from an object using dot notation
// Example: getNestedValue(obj, 'cancelReasons.1') -> obj.cancelReasons['1']
const getNestedValue = (obj: any, path: string): any => {
  const keys = path.split('.');
  let value = obj;

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return undefined;
    }
  }

  return value;
};

// Get localized value with fallback chain: step locales -> global locales -> key
const getLocalizedValue = (
  translationKey: string,
  locale: string,
  stepLocales: Record<string, any>,
  globalLocales?: { [locale: string]: Record<string, string> }
): string => {
  // Try step-specific locales first
  let value = getNestedValue(stepLocales, translationKey);

  // Fall back to global locales if not found in step
  if (value === undefined && globalLocales) {
    const globalDict = globalLocales[locale] || {};
    value = getNestedValue(globalDict, translationKey);
  }

  // If still undefined, fall back to the key
  if (value === undefined) {
    value = translationKey;
  }

  return value;
};

// recreating this logic from smi-store since importing from smi-store will increase bundle size significantly
export const getLocaleMatch = (locale: string, availableLocales: string[]): string => {
  return (
    availableLocales.find(l => l.toLowerCase() === locale.toLowerCase()) ||
    availableLocales.find(l => l.toLowerCase().substring(0, 2) === locale.toLowerCase().substring(0, 2)) ||
    availableLocales[0]
  );
};

type LocalizedTextFn = {
  /** Look up a localized text value. Returns the key itself if not found. */
  (t: string): string;
  /** With fallbackToKey: false, returns null instead of the key when not found (for data access like URLs). */
  (t: string, opts: { fallbackToKey: false }): string | null;
};

export const useMakeLocalizedText = (): LocalizedTextFn => {
  const rawLocale = useAppSelector(state => state.locale);
  const { currentStep } = useStepContext();
  const config = useConfigContext();

  const matchingLocale = getLocaleMatch(rawLocale, Object.keys(config.locales));
  const params = currentStep?.parameters;
  const stepLocales = params && 'locales' in params ? params.locales[matchingLocale] ?? {} : {};

  return ((t: string, opts?: { fallbackToKey: false }) => {
    const value = getLocalizedValue(t, matchingLocale, stepLocales, config.locales);
    if (opts?.fallbackToKey === false && value === t) return null;
    return value;
  }) as LocalizedTextFn;
};

// Replace parameters in the format {paramName} with values from parameters object
const replaceParameters = (text: string, parameters?: Record<string, string>): string => {
  if (!parameters) return text;
  return text.replace(/\{(\w+)\}/g, (match, key) => parameters[key] ?? match);
};

export const LocaleText = ({ t, parameters }: { t: string; parameters?: Record<string, string> }) => {
  const value = useMakeLocalizedText()(t);

  // Handle array values - render as a list
  if (Array.isArray(value)) {
    return (
      <ul className="og-locale-list">
        {value.map((item, index) => (
          <li key={index}>{replaceParameters(item, parameters)}</li>
        ))}
      </ul>
    );
  }

  return <>{replaceParameters(value, parameters)}</>;
};
